import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import { Nullable } from "../../../../Types/Common";
import { EmployeeDetails } from "../../../../Types/UserProfileDetails";
import { getEmployeeTabs } from "../../utils/tabs.config";

export type EmployeeNotificationType = "success" | "error" | "warning";

export interface IEmployeeNotificationPayload {
  type: EmployeeNotificationType;
  title: string;
  message: string;
  source: string | null;
}

export interface IEmployeeNotificationFormState {
  show: boolean;
  type: "success" | "error" | "warning" | null;
  title: string;
  message: string;
  source: string | null;
}

export interface IEmployeeManagementInitialState {
  employeeProfileImage: Nullable<string>;
  employeePrimaryTabActive: number;
  employeeSecondaryTabActive: Nullable<number>;
  employeeNotificationForm: IEmployeeNotificationFormState;
  mutationResetFn: (() => void) | null;
  formResetFn: (() => void) | null;
  extraResetFns?: Record<string, (() => void) | null>;
  isOpenAlertPicModal: boolean;
}

const employeeManagementInitialState: IEmployeeManagementInitialState = {
  employeeProfileImage: null,
  employeePrimaryTabActive: 2,
  employeeSecondaryTabActive: 201,
  employeeNotificationForm: {
    show: false,
    type: null,
    title: "",
    message: "",
    source: null,
  },
  mutationResetFn: null,
  formResetFn: null,
  extraResetFns: {},
  isOpenAlertPicModal: false,
};

const employeeManagementSlice = createSlice({
  name: "employeeManagement",
  initialState: employeeManagementInitialState,
  reducers: {
    setEmployeeProfileImage: (state, action) => {
      state.employeeProfileImage = action.payload;
    },
    setEmployeePrimaryTabActive(
      state,
      action: PayloadAction<{ tabId: number; employee: EmployeeDetails | null }>
    ) {
      const { tabId, employee } = action.payload;
      state.employeePrimaryTabActive = tabId;

      const tabs = getEmployeeTabs(employee);
      const next = tabs.find((t) => t.id === tabId);
      state.employeeSecondaryTabActive = next?.children?.[0]?.id ?? null;
    },
    setEmployeeSecondaryTabActive: (state, action) => {
      state.employeeSecondaryTabActive = action.payload;
    },
    showEmployeeNotificationForm: (
      state,
      action: PayloadAction<IEmployeeNotificationPayload>
    ) => {
      state.employeeNotificationForm.show = true;
      state.employeeNotificationForm.type = action.payload.type;
      state.employeeNotificationForm.title = action.payload.title;
      state.employeeNotificationForm.message = action.payload.message;
      state.employeeNotificationForm.source = action.payload.source;
    },
    hideEmployeeNotificationForm: (state) => {
      state.employeeNotificationForm.show = false;
      state.employeeNotificationForm.type = null;
      state.employeeNotificationForm.title = "";
      state.employeeNotificationForm.message = "";
      state.employeeNotificationForm.source = null;
    },
    setResetFunctions: (
      state,
      action: PayloadAction<{
        mutationReset?: (() => void) | null;
        formReset?: (() => void) | null;
        queryReset?: (() => void) | null;
        extraResetFns?: Record<string, (() => void) | null>;
      }>
    ) => {
      state.mutationResetFn = action.payload.mutationReset ?? null;
      state.formResetFn = action.payload.formReset ?? null;
      (state as any).queryResetFn = action.payload.queryReset ?? null;
      state.extraResetFns = {
        ...state.extraResetFns,
        ...action.payload.extraResetFns,
      };
    },

    clearResetFunctions: (state) => {
      state.mutationResetFn = null;
      state.formResetFn = null;
      state.extraResetFns = {};
    },
    setIsOpenAlertPicModal: (state, action) => {
      state.isOpenAlertPicModal = action.payload;
    },
  },
});

export const {
  setEmployeeProfileImage,
  setEmployeePrimaryTabActive,
  setEmployeeSecondaryTabActive,
  showEmployeeNotificationForm,
  hideEmployeeNotificationForm,
  clearResetFunctions,
  setResetFunctions,
  setIsOpenAlertPicModal,
} = employeeManagementSlice.actions;

export default employeeManagementSlice.reducer;
